import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Receipt,
  Plus,
  Search,
  Filter,
  Printer,
  FileText,
  CreditCard,
  MessageSquare,
  CheckCheck,
  CheckCircle2,
  Calendar,
  X,
  Send,
  Zap,
  Sparkles,
  ExternalLink,
  MapPin,
  Users,
  Loader2,
  AlertCircle,
  Trash2,
  CheckSquare,
  Square,
  Wallet,
  ClipboardList
} from "lucide-react";
import { ThermalReceiptModal } from "../components/common/ThermalReceiptModal";
import { InvoicePrintModal } from "../components/common/InvoicePrintModal";
import { WhatsAppReminderModal } from "../components/common/WhatsAppReminderModal";
import { PaymentGatewayModal } from "../components/common/PaymentGatewayModal";

export const KeuanganTagihanPage = () => {
  const currentKode = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  })();

  const [tagihanList, setTagihanList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [selectedMonth, setSelectedMonth] = useState(currentKode);
  const [filterWilayah, setFilterWilayah] = useState("ALL");
  const [filterRT, setFilterRT] = useState("ALL");
  const [filterRW, setFilterRW] = useState("ALL");
  const [tagihanPage, setTagihanPage] = useState(1);
  const [tagihanPerPage, setTagihanPerPage] = useState(10);
  const [wilayahList, setWilayahList] = useState([]);
  const [pelangganAll, setPelangganAll] = useState([]);
  const [settings, setSettings] = useState({ payment_enabled: false, invoice_payment_methods: [] });
  const [payForm, setPayForm] = useState({ diskon: 0, jumlah_bayar: 0, metode: "", tanggal: "" });
  const todayStr = () => new Date().toISOString().slice(0, 10);

  // Modals state
  const [selectedThermal, setSelectedThermal] = useState(null);
  const [selectedInvoicePdf, setSelectedInvoicePdf] = useState(null);
  const [selectedWa, setSelectedWa] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [gatewayDiskon, setGatewayDiskon] = useState(0);
  const [payModalInvoice, setPayModalInvoice] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [notification, setNotification] = useState("");

  const [rekapLoading, setRekapLoading] = useState(false);

  // Bulk pay (bayar massal) state
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkPayModal, setShowBulkPayModal] = useState(false);
  const [bulkPayMetode, setBulkPayMetode] = useState("");
  const [bulkPayTanggal, setBulkPayTanggal] = useState("");
  const [bulkPayLoading, setBulkPayLoading] = useState(false);

  // Bulk generate form state
  const [bulkForm, setBulkForm] = useState({
    periode_bulan: "",
    periode_kode: "",
    jatuh_tempo: "",
    wilayah_id: "ALL",    // "ALL" = semua wilayah
    pelanggan_id: "ALL",  // "ALL" = semua pelanggan (mengikuti wilayah)
  });
  const [wilayahOptions, setWilayahOptions] = useState([]);
  const [pelangganOptions, setPelangganOptions] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState("");

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchTagihan = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ periode: selectedMonth, status: filterStatus });
      if (filterWilayah !== "ALL") params.append("wilayah_id", filterWilayah);
      if (filterRT !== "ALL") params.append("rt", filterRT);
      if (filterRW !== "ALL") params.append("rw", filterRW);
      const res = await axios.get(`${BACKEND_URL}/api/tagihan?${params.toString()}`, {
        withCredentials: true,
      });
      setTagihanList(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [wilRes, pelRes, setRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/pelanggan`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/pengaturan/aplikasi`, { withCredentials: true }),
      ]);
      setWilayahList(wilRes.data || []);
      setPelangganAll(pelRes.data || []);
      setSettings(setRes.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchTagihan();
  }, [selectedMonth, filterStatus, filterWilayah, filterRT, filterRW]);

  // Reset RT/RW when wilayah changes
  useEffect(() => {
    setFilterRT("ALL");
    setFilterRW("ALL");
  }, [filterWilayah]);

  // Derive RT/RW options from pelanggan (scoped to selected wilayah)
  const scopedPelanggan = pelangganAll.filter(
    (p) => filterWilayah === "ALL" || p.wilayah_id === filterWilayah
  );
  const rtOptions = [...new Set(scopedPelanggan.map((p) => p.rt).filter(Boolean))].sort();
  const rwOptions = [...new Set(scopedPelanggan.map((p) => p.rw).filter(Boolean))].sort();

  const buildPaymentOptions = () => {
    const opts = ["Tunai / Cash"];
    if (settings.payment_enabled) opts.push("Bayar Online (Payment Gateway)");
    (settings.invoice_payment_methods || []).forEach((b) => {
      if (b.bank) opts.push(`Transfer Bank ${b.bank}${b.no_rekening ? " - " + b.no_rekening : ""}`);
    });
    return opts;
  };

  const openPayModal = (inv) => {
    const diskon = Number(inv.diskon || 0);
    const alreadyPaid = Number(inv.jumlah_dibayar || 0);
    const net = Math.max(Number(inv.total_tagihan || 0) - diskon, 0);
    const sisa = Math.max(net - alreadyPaid, 0);
    const opts = buildPaymentOptions();
    setPayForm({ diskon, jumlah_bayar: sisa, metode: opts[0] || "Tunai / Cash", tanggal: todayStr() });
    setPayModalInvoice(inv);
  };

  const handleBayar = async () => {
    if (!payModalInvoice) return;
    if (!payForm.metode) {
      alert("Pilih metode pembayaran terlebih dahulu");
      return;
    }
    // Route online payment to the real Payment Gateway modal (QRIS / VA / checkout)
    if (payForm.metode === "Bayar Online (Payment Gateway)") {
      setGatewayDiskon(Number(payForm.diskon) || 0);
      setSelectedGateway(payModalInvoice);
      setPayModalInvoice(null);
      return;
    }
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/tagihan/${payModalInvoice.id}/bayar`,
        {
          metode_pembayaran: payForm.metode,
          diskon: Number(payForm.diskon) || 0,
          nominal_dibayar: Number(payForm.jumlah_bayar) || 0,
          catatan: `Pembayaran via ${payForm.metode}`,
          tanggal_bayar: payForm.tanggal || null,
        },
        { withCredentials: true }
      );
      setNotification(res.data.message || `Pembayaran tagihan ${payModalInvoice.no_invoice} tercatat!`);
      setPayModalInvoice(null);
      fetchTagihan();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal memproses pembayaran tagihan");
    }
  };

  const handleDeleteTagihan = async (inv) => {
    if (!window.confirm(`Hapus invoice ${inv.no_invoice} atas nama ${inv.pelanggan_nama}?\nTindakan ini tidak dapat dibatalkan.`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/tagihan/${inv.id}`, { withCredentials: true });
      setNotification(`Invoice ${inv.no_invoice} berhasil dihapus`);
      fetchTagihan();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal menghapus invoice");
    }
  };

  const handleGenerateMassal = async () => {
    if (!bulkForm.periode_kode || !bulkForm.jatuh_tempo) {
      setBulkError("Lengkapi periode dan tanggal tagihan");
      return;
    }
    setBulkError("");
    setBulkResult(null);
    setBulkLoading(true);
    try {
      const payload = {
        periode_bulan: bulkForm.periode_bulan,
        periode_kode: bulkForm.periode_kode,
        jatuh_tempo: bulkForm.jatuh_tempo,
        jatuh_tempo_hari: bulkForm.hari,
      };
      if (bulkForm.pelanggan_id !== "ALL") payload.pelanggan_ids = [bulkForm.pelanggan_id];
      else if (bulkForm.wilayah_id !== "ALL") payload.wilayah_ids = [bulkForm.wilayah_id];
      const res = await axios.post(
        `${BACKEND_URL}/api/tagihan/generate-massal`,
        payload,
        { withCredentials: true }
      );
      setBulkResult(res.data);
      fetchTagihan();
      setTimeout(() => {
        setNotification(res.data.message);
        setShowBulkModal(false);
        setBulkResult(null);
        setTimeout(() => setNotification(""), 5000);
      }, 1600);
    } catch (err) {
      setBulkError(err.response?.data?.detail || "Gagal generate massal tagihan");
    } finally {
      setBulkLoading(false);
    }
  };

  const openBulkModal = async () => {
    // Preload wilayah + pelanggan
    try {
      const [wilRes, pelRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/master/wilayah`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/pelanggan`, { withCredentials: true }),
      ]);
      setWilayahOptions(wilRes.data || []);
      setPelangganOptions((pelRes.data || []).filter((p) => p.status_layanan === "aktif"));
    } catch (err) { console.error(err); }

    // Default periode = bulan sekarang
    const now = new Date();
    const bulan = now.toLocaleDateString("id-ID", { month: "long", year: "numeric" });
    const periode_kode = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const jatuh_tempo = `${periode_kode}-10`;
    setBulkForm({
      periode_bulan: bulan,
      periode_kode,
      jatuh_tempo,
      hari: 10,
      wilayah_id: "ALL",
      pelanggan_id: "ALL",
    });
    setBulkError("");
    setBulkResult(null);
    setShowBulkModal(true);
  };

  // Filter periode: tampilkan semua bulan Januari–Desember (tahun berjalan)
  const monthOptions = (() => {
    const year = new Date().getFullYear();
    const names = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    return names.map((label, idx) => ({
      label,
      kode: `${year}-${String(idx + 1).padStart(2, "0")}`,
    }));
  })();

  // Periode untuk Generate Massal: full Januari–Desember, tahun otomatis (tahun berjalan)
  const genMonthOptions = (() => {
    const year = new Date().getFullYear();
    const names = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
    return names.map((label, idx) => ({
      label,                       // ditampilkan di dropdown (nama bulan saja)
      fullLabel: `${label} ${year}`, // disimpan di record tagihan
      kode: `${year}-${String(idx + 1).padStart(2, "0")}`,
    }));
  })();

  const tglTagihan = (kode, hari) => {
    const [y, m] = kode.split("-").map(Number);
    const last = new Date(y, m, 0).getDate();
    return `${kode}-${String(Math.min(hari, last)).padStart(2, "0")}`;
  };

  const handlePeriodeChange = (kode) => {
    const opt = genMonthOptions.find((o) => o.kode === kode);
    setBulkForm((f) => ({
      ...f,
      periode_bulan: opt?.fullLabel || f.periode_bulan,
      periode_kode: kode,
      jatuh_tempo: tglTagihan(kode, f.hari || 10),
    }));
  };

  const handleHariChange = (hari) => {
    setBulkForm((f) => ({ ...f, hari, jatuh_tempo: tglTagihan(f.periode_kode, hari), wilayah_id: "ALL", pelanggan_id: "ALL" }));
  };

  // Dropdown bertingkat: Tanggal Tagihan -> Wilayah -> Nama Pelanggan
  const hariPel = (p) => Number(p.jatuh_tempo_hari || 10);
  const pelangganByHari = pelangganOptions.filter((p) => hariPel(p) === Number(bulkForm.hari || 10));
  const hariCounts = pelangganOptions.reduce((acc, p) => { acc[hariPel(p)] = (acc[hariPel(p)] || 0) + 1; return acc; }, {});
  const wilayahCount = (wid) => pelangganByHari.filter((p) => p.wilayah_id === wid).length;
  const filteredPelangganOptions = pelangganByHari.filter(
    (p) => bulkForm.wilayah_id === "ALL" || p.wilayah_id === bulkForm.wilayah_id
  );

  const estimatedCount = (() => {
    if (bulkForm.pelanggan_id !== "ALL") return 1;
    return filteredPelangganOptions.length;
  })();

  const filteredTagihan = tagihanList.filter(
    (t) =>
      t.pelanggan_nama?.toLowerCase().includes(search.toLowerCase()) ||
      t.no_invoice?.toLowerCase().includes(search.toLowerCase()) ||
      t.pelanggan_no_hp?.includes(search)
  );

  const tagihanTotalPages = Math.max(1, Math.ceil(filteredTagihan.length / tagihanPerPage));
  const safeTagihanPage = Math.min(tagihanPage, tagihanTotalPages);
  const pagedTagihan = filteredTagihan.slice((safeTagihanPage - 1) * tagihanPerPage, safeTagihanPage * tagihanPerPage);

  useEffect(() => { setTagihanPage(1); setSelectedIds([]); }, [search, selectedMonth, filterStatus, filterWilayah, filterRT, filterRW, tagihanPerPage]);

  const totalNominal = filteredTagihan.reduce((acc, t) => acc + t.total_tagihan, 0);
  const totalLunas = filteredTagihan
    .filter((t) => t.status === "PAID")
    .reduce((acc, t) => acc + t.total_tagihan, 0);
  const totalBelumBayar = filteredTagihan
    .filter((t) => t.status !== "PAID")
    .reduce((acc, t) => acc + (t.status === "PARTIAL" ? Number(t.sisa_tagihan || 0) : Number(t.total_tagihan || 0)), 0);

  // ===== Bayar Massal (bulk pay) helpers =====
  const unpaidFiltered = filteredTagihan.filter((t) => t.status !== "PAID");
  const allUnpaidSelected = unpaidFiltered.length > 0 && unpaidFiltered.every((t) => selectedIds.includes(t.id));
  const selectedInvoices = filteredTagihan.filter((t) => selectedIds.includes(t.id));
  const selectedTotal = selectedInvoices.reduce(
    (a, t) => a + (t.status === "PARTIAL" ? Number(t.sisa_tagihan || 0) : Math.max(Number(t.total_tagihan || 0) - Number(t.diskon || 0), 0)),
    0
  );

  const toggleSelectAll = () => {
    if (allUnpaidSelected) setSelectedIds([]);
    else setSelectedIds(unpaidFiltered.map((t) => t.id));
  };
  const toggleOne = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const openBulkPay = () => {
    const opts = buildPaymentOptions().filter((o) => o !== "Bayar Online (Payment Gateway)");
    setBulkPayMetode(opts[0] || "Tunai / Cash");
    setBulkPayTanggal(todayStr());
    setShowBulkPayModal(true);
  };

  const handleBayarMassal = async () => {
    setBulkPayLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/tagihan/bayar-massal`,
        { tagihan_ids: selectedIds, metode_pembayaran: bulkPayMetode, tanggal_bayar: bulkPayTanggal || null },
        { withCredentials: true }
      );
      setNotification(res.data.message || "Pembayaran massal berhasil");
      setShowBulkPayModal(false);
      setSelectedIds([]);
      fetchTagihan();
      setTimeout(() => setNotification(""), 5000);
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal memproses pembayaran massal");
    } finally {
      setBulkPayLoading(false);
    }
  };

  // ===== Cetak Rekap Tagihan (lembar tarik penarik) =====
  const handleCetakRekap = async () => {
    setRekapLoading(true);
    try {
      const params = new URLSearchParams({ periode: selectedMonth, status: filterStatus });
      if (filterWilayah !== "ALL") params.append("wilayah_id", filterWilayah);
      if (filterRT !== "ALL") params.append("rt", filterRT);
      if (filterRW !== "ALL") params.append("rw", filterRW);
      const [rekapRes, brandRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/tagihan/rekap?${params.toString()}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/branding`, { withCredentials: true }).catch(() => ({ data: {} })),
      ]);
      const data = rekapRes.data;
      if (!data.rows || data.rows.length === 0) {
        alert("Tidak ada data tagihan untuk filter yang dipilih.");
        return;
      }
      printRekap(data, brandRes.data || {});
    } catch (err) {
      alert(err.response?.data?.detail || "Gagal membuat rekap tagihan");
    } finally {
      setRekapLoading(false);
    }
  };

  const printRekap = (data, brand) => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");
    const perus = brand.perusahaan || {};
    const namaPerus = perus.nama_isp || brand.app_name || "REKAP TAGIHAN";
    const logo = brand.app_logo || "";
    const monthLabel = monthOptions.find((o) => o.kode === selectedMonth)?.label || selectedMonth;
    const wilayahLabel = filterWilayah === "ALL" ? "Semua Wilayah" : (wilayahList.find((w) => w.id === filterWilayah)?.nama_wilayah || "-");
    const rtLabel = filterRT === "ALL" ? "Semua" : `RT ${filterRT}`;
    const rwLabel = filterRW === "ALL" ? "Semua" : `RW ${filterRW}`;
    const statusLabel = filterStatus === "ALL" ? "Semua Status" : filterStatus === "PAID" ? "Lunas" : "Belum Bayar";

    const rowsHtml = data.rows.map((r, i) => {
      const alamat = [r.alamat, r.wilayah_nama, r.rt ? `RT ${r.rt}` : "", r.rw ? `RW ${r.rw}` : ""].filter(Boolean).join(" / ");
      return `<tr>
        <td class="c">${i + 1}</td>
        <td>${esc(r.nama)}</td>
        <td class="sm">${esc(alamat)}</td>
        <td class="r">${rupiah(r.jumlah_tagihan)}</td>
        <td class="r">${r.tunggakan_bulan_lalu > 0 ? rupiah(r.tunggakan_bulan_lalu) : "-"}</td>
        <td class="r b">${rupiah(r.total)}</td>
        <td class="paraf"></td>
      </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Rekap Tagihan ${esc(monthLabel)}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
      .head { display: flex; align-items: center; gap: 14px; border-bottom: 2px solid #111; padding-bottom: 10px; margin-bottom: 12px; }
      .head img { height: 52px; width: auto; }
      .head h1 { font-size: 18px; margin: 0; }
      .head p { font-size: 11px; margin: 2px 0 0; color: #444; }
      .title { text-align: center; margin: 6px 0 12px; }
      .title h2 { font-size: 15px; margin: 0; text-transform: uppercase; letter-spacing: .5px; }
      .meta { font-size: 11px; margin: 10px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 2px 24px; }
      .meta span b { display: inline-block; min-width: 90px; }
      table { width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 6px; }
      th, td { border: 1px solid #333; padding: 5px 6px; vertical-align: top; }
      th { background: #eee; text-align: left; font-size: 10px; text-transform: uppercase; }
      td.c { text-align: center; width: 28px; }
      td.r { text-align: right; white-space: nowrap; font-family: 'Courier New', monospace; }
      td.b { font-weight: bold; }
      td.sm { font-size: 10px; }
      td.paraf { width: 90px; }
      tfoot td { font-weight: bold; background: #f4f4f4; }
      .foot { margin-top: 22px; font-size: 11px; display: flex; justify-content: flex-end; }
      .sign { text-align: center; width: 200px; }
      .sign .line { margin-top: 60px; border-top: 1px solid #111; padding-top: 4px; }
      @media print { body { margin: 12mm; } .noprint { display: none; } }
    </style></head><body>
      <div class="head">
        ${logo ? `<img src="${esc(logo)}" alt="logo" />` : ""}
        <div>
          <h1>${esc(namaPerus)}</h1>
          ${perus.alamat ? `<p>${esc(perus.alamat)}</p>` : ""}
          ${(perus.telepon || perus.whatsapp) ? `<p>Telp/WA: ${esc(perus.telepon || perus.whatsapp)}</p>` : ""}
        </div>
      </div>
      <div class="title"><h2>Rekap Tagihan Pelanggan</h2></div>
      <div class="meta">
        <span><b>Periode</b>: ${esc(monthLabel)}</span>
        <span><b>Status</b>: ${esc(statusLabel)}</span>
        <span><b>Wilayah</b>: ${esc(wilayahLabel)}</span>
        <span><b>RT / RW</b>: ${esc(rtLabel)} / ${esc(rwLabel)}</span>
        <span><b>Jumlah</b>: ${data.count} pelanggan</span>
        <span><b>Dicetak</b>: ${new Date().toLocaleString("id-ID")}</span>
      </div>
      <table>
        <thead><tr>
          <th class="c">No</th>
          <th>Nama</th>
          <th>Alamat / Wilayah / RT / RW</th>
          <th style="text-align:right">Jumlah Tagihan</th>
          <th style="text-align:right">Tunggakan Bln Lalu</th>
          <th style="text-align:right">Total</th>
          <th>Paraf Penarik</th>
        </tr></thead>
        <tbody>${rowsHtml}</tbody>
        <tfoot><tr>
          <td colspan="3" style="text-align:right">TOTAL KESELURUHAN</td>
          <td class="r">${rupiah(data.total_jumlah_tagihan)}</td>
          <td class="r">${rupiah(data.total_tunggakan)}</td>
          <td class="r">${rupiah(data.total_keseluruhan)}</td>
          <td></td>
        </tr></tfoot>
      </table>
      <div class="foot">
        <div class="sign">
          <div>Petugas Penarik,</div>
          <div class="line">( ............................... )</div>
        </div>
      </div>
      <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
    </body></html>`;

    const win = window.open("", "_blank");
    if (!win) {
      alert("Popup diblokir browser. Izinkan popup untuk mencetak rekap.");
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };


  return (
    <div className="space-y-6" data-testid="tagihan-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold uppercase tracking-wider">
              Billing Management
            </span>
            <span className="text-xs text-slate-400">Periode: {selectedMonth}</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">
            Data Tagihan Pelanggan (Invoices)
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Cetak struk thermal, invoice PDF, reminder WhatsApp & verifikasi Payment Gateway online.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <button
            onClick={handleCetakRekap}
            disabled={rekapLoading}
            data-testid="cetak-rekap-btn"
            className="px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-cyan-600/30 flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {rekapLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
            <span>Cetak Rekap Tagihan</span>
          </button>
          <button
            onClick={openBulkModal}
            data-testid="generate-massal-btn"
            className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-amber-600/30 flex items-center gap-2 transition-all"
          >
            <Sparkles className="w-4 h-4" />
            <span>Generate Tagihan Massal</span>
          </button>
        </div>
      </div>

      {notification && (
        <div className="p-3 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 rounded-xl text-xs flex items-center gap-2 animate-fadeIn">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          <span>{notification}</span>
        </div>
      )}

      {/* Summary Chips */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800">
          <p className="text-xs text-slate-400 font-semibold">Total Tagihan Periode Ini</p>
          <p className="text-xl font-black text-white mt-1 font-mono">
            Rp {totalNominal.toLocaleString("id-ID")}
          </p>
          <span className="text-[11px] text-slate-500">{filteredTagihan.length} Invoice diterbitkan</span>
        </div>

        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800">
          <p className="text-xs text-slate-400 font-semibold">Sudah Lunas (PAID)</p>
          <p className="text-xl font-black text-emerald-400 mt-1 font-mono">
            Rp {totalLunas.toLocaleString("id-ID")}
          </p>
          <span className="text-[11px] text-emerald-400/80 font-semibold">
            {filteredTagihan.filter((t) => t.status === "PAID").length} Pelanggan Telah Membayar
          </span>
        </div>

        <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800">
          <p className="text-xs text-slate-400 font-semibold">Belum Bayar (UNPAID)</p>
          <p className="text-xl font-black text-rose-400 mt-1 font-mono">
            Rp {totalBelumBayar.toLocaleString("id-ID")}
          </p>
          <span className="text-[11px] text-rose-400/80 font-semibold">
            {filteredTagihan.filter((t) => t.status !== "PAID").length} Menunggu Pembayaran
          </span>
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Cari no invoice, nama pelanggan, no HP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Month Selector (dynamic, defaults to current month) */}
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            data-testid="filter-bulan"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold"
          >
            {monthOptions.map((o) => (
              <option key={o.kode} value={o.kode}>{o.label}</option>
            ))}
          </select>

          {/* Wilayah filter */}
          <select
            value={filterWilayah}
            onChange={(e) => setFilterWilayah(e.target.value)}
            data-testid="filter-wilayah"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold"
          >
            <option value="ALL">Semua Wilayah</option>
            {wilayahList.map((w) => (
              <option key={w.id} value={w.id}>{w.nama_wilayah}</option>
            ))}
          </select>

          {/* RT filter */}
          <select
            value={filterRT}
            onChange={(e) => setFilterRT(e.target.value)}
            data-testid="filter-rt"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold"
          >
            <option value="ALL">Semua RT</option>
            {rtOptions.map((rt) => (
              <option key={rt} value={rt}>{`RT ${rt}`}</option>
            ))}
          </select>

          {/* RW filter */}
          <select
            value={filterRW}
            onChange={(e) => setFilterRW(e.target.value)}
            data-testid="filter-rw"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold"
          >
            <option value="ALL">Semua RW</option>
            {rwOptions.map((rw) => (
              <option key={rw} value={rw}>{`RW ${rw}`}</option>
            ))}
          </select>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            data-testid="filter-status"
            className="px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-semibold"
          >
            <option value="ALL">Semua Status</option>
            <option value="UNPAID">Belum Bayar (UNPAID)</option>
            <option value="PAID">Lunas (PAID)</option>
          </select>
        </div>
      </div>

      {/* Bulk pay action bar */}
      {selectedIds.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/40 shadow-xl animate-fadeIn" data-testid="bulk-pay-bar">
          <div className="flex items-center gap-2 text-xs text-emerald-200">
            <CheckSquare className="w-4 h-4 text-emerald-400" />
            <span><b className="text-white">{selectedIds.length}</b> tagihan dipilih · Total tagih: <b className="text-white font-mono">Rp {selectedTotal.toLocaleString("id-ID")}</b></span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelectedIds([])} data-testid="clear-selection-btn" className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-xl">Batal Pilih</button>
            <button onClick={openBulkPay} data-testid="open-bulk-pay-btn" className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/30 flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Bayar Semua Terpilih ({selectedIds.length})
            </button>
          </div>
        </div>
      )}

      {/* Invoices Table */}
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs" data-testid="tagihan-table">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400 font-semibold">
                <th className="py-3 px-3 w-10 text-center">
                  <input
                    type="checkbox"
                    checked={allUnpaidSelected}
                    onChange={toggleSelectAll}
                    disabled={unpaidFiltered.length === 0}
                    data-testid="select-all-tagihan"
                    title="Pilih semua tagihan belum lunas"
                    className="w-4 h-4 accent-emerald-500 cursor-pointer disabled:opacity-40"
                  />
                </th>
                <th className="py-3 px-3">No. Invoice & Tanggal</th>
                <th className="py-3 px-3">Nama Pelanggan</th>
                <th className="py-3 px-3">Paket Internet</th>
                <th className="py-3 px-3">Total Tagihan</th>
                <th className="py-3 px-3">Jatuh Tempo</th>
                <th className="py-3 px-3 text-center">Status</th>
                <th className="py-3 px-3 text-right">Aksi & Pembayaran</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {pagedTagihan.map((inv) => (
                <tr key={inv.id} className={`hover:bg-slate-900/40 transition-colors ${selectedIds.includes(inv.id) ? "bg-emerald-500/5" : ""}`}>
                  <td className="py-3 px-3 text-center">
                    {inv.status !== "PAID" ? (
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(inv.id)}
                        onChange={() => toggleOne(inv.id)}
                        data-testid={`select-tagihan-${inv.id}`}
                        className="w-4 h-4 accent-emerald-500 cursor-pointer"
                      />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500/40 mx-auto" title="Sudah lunas" />
                    )}
                  </td>
                  <td className="py-3 px-3">
                    <p className="font-mono font-bold text-white text-xs">
                      {inv.no_invoice}
                    </p>
                    <span className="text-[10px] text-slate-400">
                      Terbit: {inv.tanggal_terbit}
                    </span>
                  </td>

                  <td className="py-3 px-3">
                    <p className="font-bold text-slate-100">{inv.pelanggan_nama}</p>
                    <span className="text-[10px] text-slate-400">{inv.pelanggan_no_hp}</span>
                  </td>

                  <td className="py-3 px-3">
                    <span className="px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-medium text-[11px]">
                      {inv.paket_nama}
                    </span>
                  </td>

                  <td className="py-3 px-3 font-mono font-bold text-sm text-cyan-400">
                    Rp {Number(inv.total_tagihan).toLocaleString("id-ID")}
                  </td>

                  <td className="py-3 px-3 font-mono text-[11px] text-amber-400 font-semibold">
                    {inv.jatuh_tempo}
                  </td>

                  <td className="py-3 px-3 text-center">
                    <span
                      className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                        inv.status === "PAID"
                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                          : inv.status === "PARTIAL"
                          ? "bg-amber-500/20 text-amber-300 border border-amber-500/30"
                          : "bg-rose-500/20 text-rose-300 border border-rose-500/30"
                      }`}
                    >
                      {inv.status === "PARTIAL" ? "SEBAGIAN" : inv.status}
                    </span>
                    {inv.status === "PARTIAL" && Number(inv.sisa_tagihan) > 0 && (
                      <span className="block text-[9px] text-amber-400 mt-0.5 font-mono">
                        Sisa: Rp {Number(inv.sisa_tagihan).toLocaleString("id-ID")}
                      </span>
                    )}
                    {inv.metode_pembayaran && (
                      <span className="block text-[9px] text-slate-400 mt-0.5">
                        {inv.metode_pembayaran}
                      </span>
                    )}
                  </td>

                  <td className="py-3 px-3 text-right">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      {inv.status !== "PAID" && (
                        <>
                          <button
                            onClick={() => openPayModal(inv)}
                            title="Bayar Kasir / Cash / Transfer"
                            data-testid={`bayar-cash-${inv.id}`}
                            className="px-2 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg text-xs font-bold transition-colors shadow"
                          >
                            Bayar
                          </button>
                          {settings.payment_enabled && (
                            <button
                              onClick={() => setSelectedGateway(inv)}
                              title="Payment Gateway Online"
                              data-testid={`gateway-btn-${inv.id}`}
                              className="p-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
                            >
                              <CreditCard className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </>
                      )}

                      {inv.status === "PAID" ? (
                        <button
                          onClick={() => setSelectedWa({ ...inv, _waType: "lunas" })}
                          title="Kirim Konfirmasi Tagihan Lunas"
                          data-testid={`wa-lunas-${inv.id}`}
                          className="p-1 bg-sky-950/60 hover:bg-sky-900 text-sky-300 rounded-lg transition-colors border border-sky-700"
                        >
                          <CheckCheck className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => setSelectedWa(inv)}
                          title="Kirim Reminder WhatsApp"
                          data-testid={`wa-reminder-${inv.id}`}
                          className="p-1 bg-emerald-950/60 hover:bg-emerald-900 text-emerald-400 rounded-lg transition-colors border border-emerald-800"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                        </button>
                      )}

                      <button
                        onClick={() => setSelectedThermal(inv)}
                        title="Cetak Struk Thermal 58mm"
                        data-testid={`print-thermal-${inv.id}`}
                        className="p-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
                      >
                        <Printer className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => setSelectedInvoicePdf(inv)}
                        title="Cetak Invoice PDF / A4"
                        data-testid={`print-pdf-${inv.id}`}
                        className="p-1 bg-slate-800 hover:bg-slate-700 text-cyan-400 rounded-lg transition-colors"
                      >
                        <FileText className="w-3.5 h-3.5" />
                      </button>

                      <button
                        onClick={() => handleDeleteTagihan(inv)}
                        title="Hapus Invoice"
                        data-testid={`delete-invoice-${inv.id}`}
                        className="p-1 bg-rose-950/60 hover:bg-rose-900 text-rose-400 rounded-lg transition-colors border border-rose-800"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-slate-800" data-testid="tagihan-pagination">
          <span className="text-[11px] text-slate-400">
            Menampilkan {filteredTagihan.length === 0 ? 0 : (safeTagihanPage - 1) * tagihanPerPage + 1}–{Math.min(safeTagihanPage * tagihanPerPage, filteredTagihan.length)} dari {filteredTagihan.length} invoice
          </span>
          <div className="flex items-center gap-2">
            <select
              value={tagihanPerPage}
              onChange={(e) => setTagihanPerPage(Number(e.target.value))}
              data-testid="tagihan-per-page-select"
              className="px-2 py-1.5 bg-slate-900 border border-slate-700 rounded-lg text-[11px] text-slate-200"
            >
              <option value={10}>10 / halaman</option>
              <option value={20}>20 / halaman</option>
            </select>
            <button
              onClick={() => setTagihanPage((p) => Math.max(1, p - 1))}
              disabled={safeTagihanPage <= 1}
              data-testid="tagihan-prev-page-btn"
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-lg text-[11px] font-bold text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              &lsaquo; Sebelumnya
            </button>
            <span className="text-[11px] text-slate-400" data-testid="tagihan-page-indicator">Hal {safeTagihanPage} / {tagihanTotalPages}</span>
            <button
              onClick={() => setTagihanPage((p) => Math.min(tagihanTotalPages, p + 1))}
              disabled={safeTagihanPage >= tagihanTotalPages}
              data-testid="tagihan-next-page-btn"
              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-700 rounded-lg text-[11px] font-bold text-slate-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Selanjutnya &rsaquo;
            </button>
          </div>
        </div>
      </div>

      {/* Payment Form Modal (Diskon + Jumlah Bayar + Metode dropdown) */}
      {payModalInvoice && (() => {
        const diskon = Number(payForm.diskon) || 0;
        const alreadyPaid = Number(payModalInvoice.jumlah_dibayar || 0);
        const net = Math.max(Number(payModalInvoice.total_tagihan || 0) - diskon, 0);
        const thisPay = Number(payForm.jumlah_bayar) || 0;
        const sisa = Math.max(net - alreadyPaid - thisPay, 0);
        const paymentOptions = buildPaymentOptions();
        const willBeLunas = sisa <= 0;
        return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bayar-modal">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-800 p-4 bg-[#0a1120]">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-emerald-400" />
                <span>Form Pembayaran Tagihan</span>
              </h3>
              <button onClick={() => setPayModalInvoice(null)} className="text-slate-400 hover:text-white p-1" data-testid="close-bayar-modal">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="p-3 bg-slate-900 rounded-xl text-xs space-y-1">
                <p className="text-slate-400">No. Invoice: <span className="text-white font-bold">{payModalInvoice.no_invoice}</span></p>
                <p className="text-slate-400">Pelanggan: <span className="text-white font-bold">{payModalInvoice.pelanggan_nama}</span></p>
                {alreadyPaid > 0 && (
                  <p className="text-amber-400">Sudah dibayar sebelumnya: <span className="font-bold font-mono">Rp {alreadyPaid.toLocaleString("id-ID")}</span></p>
                )}
              </div>

              {/* Total Tagihan (read only) */}
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Total Tagihan</label>
                <div className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-sm text-cyan-400 font-mono font-bold" data-testid="pay-total">
                  Rp {Number(payModalInvoice.total_tagihan || 0).toLocaleString("id-ID")}
                </div>
              </div>

              {/* Diskon */}
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Diskon (Rp)</label>
                <input
                  type="number"
                  min="0"
                  value={payForm.diskon}
                  onChange={(e) => setPayForm((f) => ({ ...f, diskon: e.target.value }))}
                  placeholder="0"
                  data-testid="pay-diskon-input"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-amber-500"
                />
                <p className="text-[10px] text-slate-500 mt-1">Isi jika pelanggan mendapat potongan harga. Tagihan setelah diskon: <span className="text-slate-300 font-mono">Rp {net.toLocaleString("id-ID")}</span></p>
              </div>

              {/* Jumlah Bayar */}
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Jumlah Bayar (Rp)</label>
                <input
                  type="number"
                  min="0"
                  value={payForm.jumlah_bayar}
                  onChange={(e) => setPayForm((f) => ({ ...f, jumlah_bayar: e.target.value }))}
                  placeholder="0"
                  data-testid="pay-jumlah-input"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500"
                />
                <p className="text-[10px] text-slate-500 mt-1">Isi nominal yang dibayar. Boleh sebagian (cicilan).</p>
              </div>

              {/* Sisa Tagihan */}
              <div className={`p-3 rounded-xl border flex items-center justify-between ${willBeLunas ? "bg-emerald-500/10 border-emerald-500/30" : "bg-amber-500/10 border-amber-500/30"}`}>
                <span className="text-xs font-semibold text-slate-300">Sisa Tagihan</span>
                <span className={`text-sm font-mono font-bold ${willBeLunas ? "text-emerald-400" : "text-amber-400"}`} data-testid="pay-sisa">
                  {willBeLunas ? "LUNAS — Rp 0" : `Rp ${sisa.toLocaleString("id-ID")}`}
                </span>
              </div>

              {/* Tanggal Bayar */}
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-cyan-400" /> Tanggal Bayar
                </label>
                <input
                  type="date"
                  value={payForm.tanggal}
                  onChange={(e) => setPayForm((f) => ({ ...f, tanggal: e.target.value }))}
                  data-testid="pay-tanggal-input"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500 [color-scheme:dark]"
                />
                <p className="text-[10px] text-slate-500 mt-1">Kosongkan / biarkan hari ini jika bayar sekarang. Berguna agar laporan bulanan rapi.</p>
              </div>

              {/* Metode Pembayaran dropdown */}
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Metode Pembayaran</label>
                <select
                  value={payForm.metode}
                  onChange={(e) => setPayForm((f) => ({ ...f, metode: e.target.value }))}
                  data-testid="pay-metode-select"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  {paymentOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
                {!settings.payment_enabled && (
                  <p className="text-[10px] text-slate-500 mt-1">Opsi "Bayar Online" muncul bila Payment Gateway diaktifkan di Pengaturan Aplikasi.</p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-slate-800 bg-[#0a1120]">
              <button
                onClick={() => setPayModalInvoice(null)}
                className="px-4 py-2 bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl hover:bg-slate-700"
              >
                Batal
              </button>
              <button
                onClick={handleBayar}
                disabled={thisPay <= 0}
                data-testid="confirm-bayar-btn"
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/30 flex items-center gap-2 disabled:opacity-50"
              >
                <CheckCircle2 className="w-4 h-4" />
                {willBeLunas ? "Simpan & Lunasi" : "Simpan Pembayaran Sebagian"}
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Bulk Pay (Bayar Massal) Modal */}
      {showBulkPayModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-pay-modal">
          <div className="bg-[#0f172a] border border-emerald-500/40 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-800 p-4 bg-[#0a1120]">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Wallet className="w-4 h-4 text-emerald-400" />
                <span>Bayar Tagihan Massal</span>
              </h3>
              <button onClick={() => setShowBulkPayModal(false)} className="text-slate-400 hover:text-white p-1" data-testid="close-bulk-pay-modal">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-xs space-y-1">
                <p className="text-slate-300">Jumlah tagihan dipilih: <b className="text-white">{selectedIds.length}</b></p>
                <p className="text-slate-300">Total akan dilunasi: <b className="text-emerald-300 font-mono">Rp {selectedTotal.toLocaleString("id-ID")}</b></p>
                <p className="text-[10px] text-slate-400 mt-1">Semua tagihan terpilih akan ditandai <b className="text-emerald-300">LUNAS</b> penuh. Pelanggan yang terisolir otomatis diaktifkan kembali.</p>
              </div>
              <div className="max-h-40 overflow-y-auto rounded-lg bg-slate-950 border border-slate-800 p-2 space-y-1" data-testid="bulk-pay-list">
                {selectedInvoices.map((t) => (
                  <div key={t.id} className="text-[11px] text-slate-400 flex justify-between gap-2">
                    <span className="truncate">{t.pelanggan_nama} · <span className="font-mono">{t.no_invoice}</span></span>
                    <span className="font-mono text-slate-300 shrink-0">Rp {(t.status === "PARTIAL" ? Number(t.sisa_tagihan || 0) : Math.max(Number(t.total_tagihan || 0) - Number(t.diskon || 0), 0)).toLocaleString("id-ID")}</span>
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1">
                  <Calendar className="w-3 h-3 text-cyan-400" /> Tanggal Bayar
                </label>
                <input
                  type="date"
                  value={bulkPayTanggal}
                  onChange={(e) => setBulkPayTanggal(e.target.value)}
                  data-testid="bulk-pay-tanggal-input"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white font-mono focus:outline-none focus:ring-1 focus:ring-cyan-500 [color-scheme:dark]"
                />
                <p className="text-[10px] text-slate-500 mt-1">Semua tagihan terpilih dicatat lunas pada tanggal ini. Kosong = hari ini.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">Metode Pembayaran</label>
                <select
                  value={bulkPayMetode}
                  onChange={(e) => setBulkPayMetode(e.target.value)}
                  data-testid="bulk-pay-metode-select"
                  className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-xl text-sm text-white focus:outline-none focus:ring-1 focus:ring-emerald-500"
                >
                  {buildPaymentOptions().filter((o) => o !== "Bayar Online (Payment Gateway)").map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-800 bg-[#0a1120]">
              <button onClick={() => setShowBulkPayModal(false)} className="px-4 py-2 bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl hover:bg-slate-700">Batal</button>
              <button
                onClick={handleBayarMassal}
                disabled={bulkPayLoading || selectedIds.length === 0}
                data-testid="confirm-bulk-pay-btn"
                className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-emerald-600/30 flex items-center gap-2 disabled:opacity-50"
              >
                {bulkPayLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {bulkPayLoading ? "Memproses..." : `Lunasi ${selectedIds.length} Tagihan`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Generate Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="bulk-generate-modal">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-slate-800 p-4 bg-[#0a1120]">
              <h3 className="text-sm font-bold text-white flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <span>Generate Tagihan Massal</span>
              </h3>
              <button onClick={() => setShowBulkModal(false)} className="text-slate-400 hover:text-white p-1">
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              {/* Success / Error */}
              {bulkResult && (
                <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-xs flex items-start gap-2" data-testid="bulk-success">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold">{bulkResult.message}</p>
                    <p className="text-emerald-400/80 mt-1">{bulkResult.count} tagihan baru dibuat{bulkResult.skipped > 0 ? `, ${bulkResult.skipped} sudah ada dan dilewati` : ""}{bulkResult.skipped_belum_daftar > 0 ? `, ${bulkResult.skipped_belum_daftar} belum terdaftar di periode ini` : ""}</p>
                  </div>
                </div>
              )}
              {bulkError && (
                <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/40 text-rose-300 text-xs flex items-start gap-2" data-testid="bulk-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>{bulkError}</span>
                </div>
              )}

              {/* Periode & Jatuh Tempo */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-cyan-400" /> Periode Bulan
                  </label>
                  <select
                    value={bulkForm.periode_kode}
                    onChange={(e) => handlePeriodeChange(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200"
                    data-testid="bulk-periode-select"
                  >
                    {genMonthOptions.map((o) => <option key={o.kode} value={o.kode}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 block mb-1 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-amber-400" /> Tanggal Tagihan / Jatuh Tempo
                  </label>
                  <select
                    value={bulkForm.hari || 10}
                    onChange={(e) => handleHariChange(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200 font-mono"
                    data-testid="bulk-tanggal-select"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                      <option key={d} value={d}>Tanggal {d} ({hariCounts[d] || 0} pelanggan)</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Dropdown bertingkat: Wilayah -> Nama Pelanggan */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-300 mb-1 flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-purple-400" /> Wilayah
                  </label>
                  <select
                    value={bulkForm.wilayah_id}
                    onChange={(e) => setBulkForm((f) => ({ ...f, wilayah_id: e.target.value, pelanggan_id: "ALL" }))}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200"
                    data-testid="bulk-wilayah-select"
                  >
                    <option value="ALL">Semua Wilayah ({pelangganByHari.length} pelanggan)</option>
                    {wilayahOptions.map((w) => (
                      <option key={w.id} value={w.id}>{w.kode_wilayah} — {w.nama_wilayah} ({wilayahCount(w.id)})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-300 mb-1 flex items-center gap-1">
                    <Users className="w-3 h-3 text-emerald-400" /> Nama Pelanggan
                  </label>
                  <select
                    value={bulkForm.pelanggan_id}
                    onChange={(e) => setBulkForm((f) => ({ ...f, pelanggan_id: e.target.value }))}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-200"
                    data-testid="bulk-pelanggan-select"
                  >
                    <option value="ALL">Semua Pelanggan Aktif{bulkForm.wilayah_id !== "ALL" ? " di Wilayah Ini" : ""} ({filteredPelangganOptions.length})</option>
                    {filteredPelangganOptions.map((p) => (
                      <option key={p.id} value={p.id}>{p.nama} — {p.no_layanan} (Rp {Number(p.paket_harga || 0).toLocaleString("id-ID")})</option>
                    ))}
                  </select>
                  <p className="text-[10px] text-slate-500 mt-1">Hanya pelanggan AKTIF dengan tanggal tagihan {bulkForm.hari || 10} yang ditampilkan &amp; digenerate.</p>
                </div>
              </div>

              {/* Summary */}
              <div className="p-3 rounded-xl bg-amber-500/5 border border-amber-500/30 text-xs text-slate-300 leading-relaxed">
                <p className="font-bold text-amber-300 mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Ringkasan</p>
                <p>Periode: <strong className="text-white">{bulkForm.periode_bulan}</strong></p>
                <p>Tanggal Tagihan: <strong className="text-white">Pelanggan tanggal {bulkForm.hari || 10}</strong> · Jatuh Tempo <strong className="text-white font-mono">{bulkForm.jatuh_tempo}</strong></p>
                <p>Wilayah: <strong className="text-white">{bulkForm.wilayah_id === "ALL" ? "Semua Wilayah" : wilayahOptions.find((w) => w.id === bulkForm.wilayah_id)?.nama_wilayah || "-"}</strong></p>
                <p>Pelanggan: <strong className="text-white">{bulkForm.pelanggan_id === "ALL" ? "Semua pelanggan aktif" : pelangganOptions.find((p) => p.id === bulkForm.pelanggan_id)?.nama || "-"}</strong></p>
                <p className="mt-2 text-amber-300">Estimasi invoice akan dibuat: <strong>{estimatedCount}</strong> tagihan</p>
                <p className="mt-1 text-[10px] text-slate-400">Catatan: pelanggan yang tanggal pendaftarannya lebih baru dari periode ini otomatis dilewati (belum jadi pelanggan saat itu), sehingga jumlah akhir bisa lebih sedikit dari estimasi.</p>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-slate-800 bg-[#0a1120]">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl hover:bg-slate-700"
                data-testid="cancel-bulk-btn"
              >
                Batal
              </button>
              <button
                onClick={handleGenerateMassal}
                disabled={bulkLoading || !!bulkResult || estimatedCount === 0}
                data-testid="confirm-bulk-generate"
                className="px-4 py-2 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white text-xs font-bold rounded-xl shadow-lg shadow-amber-600/30 flex items-center gap-2 disabled:opacity-50"
              >
                {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {bulkLoading ? "Memproses..." : bulkResult ? "Berhasil" : `Generate ${estimatedCount} Tagihan`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedThermal && (
        <ThermalReceiptModal
          invoice={selectedThermal}
          onClose={() => setSelectedThermal(null)}
        />
      )}
      {selectedInvoicePdf && (
        <InvoicePrintModal
          invoice={selectedInvoicePdf}
          onClose={() => setSelectedInvoicePdf(null)}
        />
      )}
      {selectedWa && (
        <WhatsAppReminderModal
          invoice={selectedWa}
          type={selectedWa._waType || "tagihan"}
          onClose={() => setSelectedWa(null)}
        />
      )}
      {selectedGateway && (
        <PaymentGatewayModal
          invoice={selectedGateway}
          provider={settings.payment_provider || "tripay"}
          diskon={gatewayDiskon}
          onClose={() => setSelectedGateway(null)}
          onPaymentSuccess={() => {
            fetchTagihan();
            setNotification("Pembayaran online via Gateway terkonfirmasi lunas!");
          }}
        />
      )}
    </div>
  );
};
